<template>
  <BaseModalEditCreate
    :mode="mode"
    :is-modified="isModified"
    :hide-save-button="!modifiedData.isFromManager"
    @save="onSave"
    @dismiss-changes="onDismissChanges">
    <template #form>
      <div class="space-y-2" :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <span class="font-normal text-left text-gray-600">{{ $t('visitPlans.insertDays') }}</span>
        <div class="flex flex-row gap-1">
          <div v-for="(item, index) in planDays" :key="index">
            <BaseButton
              size="xs"
              :color="item.isSelected ? 'primary' : 'light'"
              class="font-medium uppercase text-slate-500"
              @click="onDaySelected(item.index, !item.isSelected)">
              {{ item.day }}
            </BaseButton>
          </div>
        </div>
        <div class="flex flex-row gap-2">
          <BaseCheckbox v-model="selectAllDays" @click="onSelectAllDays" />
          <span class="my-auto text-sm font-normal text-left text-gray-600">{{
            $t('visitPlans.selectAll')
          }}</span>
          <p class="mx-2 my-auto text-xs text-red-500" v-if="$v.code.required.$invalid">
            {{ $v.code.required.$message }}
          </p>
        </div>
      </div>

      <div class="py-2 space-y-2" :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <span class="font-normal text-left text-gray-600">{{ $t('visitPlans.giveName') }}</span>
        <BaseInput
          v-model="modifiedData.name"
          color="base"
          show-error-as-tooltip
          :error="$v.name.required.$invalid ? $v.name.required.$message : ''" />
      </div>
      <div class="flex flex-row gap-6 py-2">
        <div class="space-y-2" :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <span class="font-semibold text-left text-gray-600">{{
            $t('visitPlans.weeklyStep')
          }}</span>
          <BaseInput
            v-model="modifiedData.weekStep"
            color="base"
            type="number"
            show-error-as-tooltip
            :error="
              $v.weekStep.required.$invalid
                ? $v.weekStep.required.$message
                : $v.weekStep.minValue.$invalid
                  ? $v.weekStep.minValue.$message
                  : ''
            " />
        </div>
        <div class="space-y-2" :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
          <span class="font-semibold text-left text-gray-600">{{
            $t('visitPlans.effectiveWeek')
          }}</span>
          <BaseInput
            v-model="modifiedData.effectiveWeek"
            color="base"
            type="number"
            flat
            show-error-as-tooltip
            :error="
              $v.effectiveWeek.required.$invalid
                ? $v.effectiveWeek.required.$message
                : $v.effectiveWeek.minValue.$invalid
                  ? $v.effectiveWeek.minValue.$message
                  : ''
            " />
        </div>
      </div>
      <div class="py-2 space-y-2" :class="{ 'pointer-events-none': !modifiedData.isFromManager }">
        <span class="font-semibold text-left text-gray-600">{{ $t('visitPlans.remarks') }}</span>
        <BaseInput class="text-wrap" v-model="modifiedData.remarks" color="base" flat />
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useToastStore } from '@/stores/toast';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers, minValue } from '@vuelidate/validators';
import { deepCopy } from '@/utils/data';

import BaseInput from '@/components/common/BaseInput.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import BaseModalEditCreate from '@/components/common/BaseModalEditCreate.vue';

import type { VisitPlan } from '@/types/Routes';
import { editVisitPlan } from '@/services/api/routes';
import BaseCheckbox from '../common/BaseCheckbox.vue';

const { t: $t } = useI18n({ useScope: 'global' });
const emit = defineEmits(['closeModal', 'save']);
const props = withDefaults(
  defineProps<{
    mode: 'add' | 'edit';
    visitPlan: VisitPlan;
  }>(),
  {}
);

const selectAllDays = ref<boolean>(false);
const modifiedData = ref<VisitPlan>(deepCopy<VisitPlan>(props.visitPlan));
const isModified = ref<boolean>(false);

onMounted(async () => {
  if (modifiedData.value.code) deserializeVisitPlanCode();
});

watch(
  () => modifiedData.value,
  (newData) => {
    validationModel.value.name = newData.name || '';
    validationModel.value.code = newData.code || '';
    validationModel.value.weekStep = newData.weekStep || 0;
    validationModel.value.effectiveWeek = newData.effectiveWeek || 0;

    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.visitPlan);
    isModified.value = valueChanged || $v.value.$invalid;
  },
  { deep: true }
);

type Days = { day: string; index: number; isSelected: boolean };
const planDays = ref<Days[]>([
  {
    day: `${$t('date.days.1')}`,
    index: 1,
    isSelected: false,
  },
  {
    day: `${$t('date.days.2')}`,
    index: 2,
    isSelected: false,
  },
  {
    day: `${$t('date.days.3')}`,
    index: 3,
    isSelected: false,
  },
  {
    day: `${$t('date.days.4')}`,
    index: 4,
    isSelected: false,
  },
  {
    day: `${$t('date.days.5')}`,
    index: 5,
    isSelected: false,
  },
  {
    day: `${$t('date.days.6')}`,
    index: 6,
    isSelected: false,
  },
  {
    day: `${$t('date.days.0')}`,
    index: 0,
    isSelected: false,
  },
]);

function onSelectAllDays() {
  selectAllDays.value = !selectAllDays.value;
  planDays.value.forEach((day) => {
    day.isSelected = selectAllDays.value;
  });
  serializeVisitPlanCode();
}

function onDaySelected(dayIndex: number, dayIsSelected: boolean) {
  const index = planDays.value.findIndex((x) => x.index == dayIndex);
  planDays.value[index] = { ...planDays.value[index], isSelected: dayIsSelected };
  serializeVisitPlanCode();
}

function onDismissChanges() {
  modifiedData.value = deepCopy<VisitPlan>(props.visitPlan);
  emit('closeModal');
}

const toastStore = useToastStore();

async function onSave() {
  $v.value.$touch();
  if ($v.value.$invalid) return;
  const res = await editVisitPlan(modifiedData.value);
  if (res) {
    emit('save', modifiedData.value);
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
    emit('closeModal');
  }
  $v.value.$reset();
}

const validationModel = ref({
  name: modifiedData.value.name || '',
  code: modifiedData.value.code || '',
  weekStep: modifiedData.value.weekStep || 0,
  effectiveWeek: modifiedData.value.effectiveWeek || 0,
});

const rules = computed(() => ({
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: { required: helpers.withMessage(`${$t('visitPlans.codeRequired')}`, required) },
  weekStep: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage(`${$t('messages.errors.greaterThan0')}`, minValue(1)),
  },
  effectiveWeek: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
    minValue: helpers.withMessage(`${$t('messages.errors.greaterThan0')}`, minValue(1)),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function serializeVisitPlanCode() {
  let visitPlanCode = '';
  if (planDays.value.every((day) => day.isSelected == false)) modifiedData.value.code = '';
  else {
    planDays.value.forEach((day) => {
      if (day.isSelected) visitPlanCode += '1';
      else visitPlanCode += '0';
    });
    modifiedData.value.code = visitPlanCode;
  }
}

function deserializeVisitPlanCode() {
  const codeChars = [...modifiedData.value.code];
  codeChars.forEach((char, index) => {
    if (char === '1') {
      onDaySelected(++index % 7, true);
    }
  });
}
</script>
